import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { Formik, FormikHelpers, Form } from "formik";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { FormInput } from "@/core/components/form-input";
import { InfoWithLabel } from "@/core/components/info-with-label";
import { Button } from "@/core/components/ui/button";
import { PhoneInput } from "@/core/components/ui/phone-input";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { formatDate } from "@/core/utils/date";
import { formatPhoneNumber } from "@/core/utils/phone";
import { CUSTOMER_STATUS } from "@/customer/constants";
import { ICustomer } from "@/customer/customer";
import { useUpsertCustomer } from "@/customer/query";

interface LeadDetailsFormProps {
  businessId: string;
  initialData: ICustomer | undefined;
}

interface CustomerFormValues {
  name: string;
  phone_number: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string(),
  phone_number: Yup.string().required("Phone number is required"),
});

export const LeadDetailsForm = ({
  businessId,
  initialData,
}: LeadDetailsFormProps) => {
  const navigate = useNavigate();
  const { updateSidebar } = useBusinessSettingsSidebar();
  const { mutate: upsertCustomer } = useUpsertCustomer(businessId);

  const {
    id: leadId,
    name,
    phone_number: phoneNumber,
    lead_referral: leadReferral,
    created_at: createdAt,
  } = initialData || {};

  const initialValues = useMemo<CustomerFormValues>(
    () => ({
      name: name || "",
      phone_number: formatPhoneNumber(phoneNumber) || "",
    }),
    [initialData],
  );

  const handleSubmit = useCallback(
    async (
      values: CustomerFormValues,
      { resetForm }: FormikHelpers<CustomerFormValues>,
    ) =>
      upsertCustomer(
        {
          ...values,
          id: leadId,
          status: CUSTOMER_STATUS.POTENTIAL,
        },
        {
          onSuccess: () => {
            resetForm({ values });

            updateSidebar({
              selectedItemId: initialData?.id,
            });

            toast.success("Customer updated successfully");
          },
          onError: (error) => {
            toast.error("Failed to update advocate", {
              description:
                error instanceof Error ? error.message : "Please try again",
            });
          },
        },
      ),
    [updateSidebar, upsertCustomer, leadId, initialData],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      className="h-full"
    >
      {({ values, setFieldValue, getFieldProps, dirty, resetForm }) => (
        <Form className="w-full">
          <Stack gap="lg" fullWidth>
            <FormInput
              label="Name"
              placeholder="Enter name"
              fullWidth
              {...getFieldProps("name")}
            />

            <PhoneInput
              name="phone_number"
              value={values.phone_number}
              onChange={(value) => setFieldValue("phone_number", value)}
              disabled
              required
              fullWidth
              disabledTooltip={
                <>
                  {" "}
                  Contact support at{" "}
                  <span className="font-semibold">support@volca.ai</span> to
                  change a customer phone number
                </>
              }
            />

            {!!leadReferral?.service_needed && (
              <InfoWithLabel label="Service needed">
                {leadReferral.service_needed}
              </InfoWithLabel>
            )}

            {createdAt && (
              <InfoWithLabel label="Date created">
                {formatDate(createdAt)}
              </InfoWithLabel>
            )}

            {!!leadReferral?.advocate?.name && (
              <Stack
                direction="row"
                justify="between"
                align="center"
                fullWidth
                className="cursor-pointer"
                onClick={() => {
                  navigate(
                    `/business-settings/${businessId}/customers/advocates`,
                  );
                  updateSidebar({
                    section: SIDEBAR_SECTIONS.advocates,
                    mode: SIDEBAR_MODES.view,
                    selectedItemId: leadReferral?.advocate?.id || undefined,
                    isOpen: true,
                  });
                }}
              >
                <InfoWithLabel label="Referred by">
                  {leadReferral.advocate.name}
                </InfoWithLabel>
                <ChevronRightIcon className="h-5 w-5" />
              </Stack>
            )}

            <Stack direction="row">
              <Button type="submit" size="sm" disabled={!dirty}>
                Save Changes
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!dirty}
                onClick={() => resetForm({ values: initialValues })}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
